import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Button, Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import CashIcon from 'mdi-react/CashIcon';
import CommentTextOutlineIcon from 'mdi-react/CommentTextOutlineIcon';
import CommentOutlineIcon from 'mdi-react/CommentOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import AccountPlusOutlineIcon from 'mdi-react/AccountPlusOutlineIcon';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import useModal from '../../../shared/hooks/useModal';
import Checkbox from '../../../shared/components/custom/Checkbox';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import CommentModal from './CommentModal';
import ShareModal from '../../../shared/components/custom/ShareModal';
import { formatDateTime, getBaseDate } from '../../../util/date-and-time';
import useConfig from '../../../util/useConfig';
import PaymentOfferModal from './PaymentOfferModal';
import useOverLimitConfirm, {
  OVER_LIMIT_DECLINED,
} from '../useOverLimitConfirm';
import {
  eventCurrency,
  groupFits,
  groupSize,
  isPaidEvent,
  paymentErrorMessage,
  placesSummary,
} from '../payments';

const WaitlistPage = ({ eventId, event, eventCounts, onChanged }) => {
  const config = useConfig();
  const currency = eventCurrency(event, config);
  const paid = isPaidEvent(event);
  // null = no limit
  const freePlaces = eventCounts ? eventCounts.freePlaces : undefined;
  const [waiters, setWaiters] = useState([]);
  // Active offers (paid events), by waiter id
  const [offers, setOffers] = useState({});
  // The waiter being offered a place
  const [offering, setOffering] = useState(null);
  const [offerBusy, setOfferBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  // The delete modal holds either one waiter's id or 'selected' (bulk)
  const [handleModal, deleteVisible, deleteTarget] = useModal();
  const { sendWithOverride, confirmModal: overLimitModal } =
    useOverLimitConfirm();
  const isSomeoneSelected = useMemo(
    () => waiters.some((waiter) => waiter.toggled),
    [waiters]
  );

  const p = partial('EventsPage');
  const s = partial('shared');
  const epp = partial('EventParticipantsPage');
  const ep = partial('EventPayments');

  const getWaitingList = async () => {
    setLoading(true);
    try {
      const [resWaiters, clubCompanies, resPending] = await Promise.all([
        Api.events.getWaitingParticipants(eventId),
        Api.companies.getClubCompanies(),
        paid
          ? Api.events.getPendingBookings(eventId).catch(() => [])
          : Promise.resolve([]),
      ]);
      setOffers(
        Object.fromEntries(
          (resPending || [])
            .filter(
              (row) =>
                row.kind === 'offer' &&
                row.status === 'holding' &&
                row.waitingParticipantId
            )
            .map((row) => [row.waitingParticipantId, row])
        )
      );
      setWaiters(
        resWaiters.map((item) => ({
          ...item,
          toggled: false,
          company: clubCompanies.find((comp) => comp.id === item.companyId),
        }))
      );
      setLoading(false);
    } catch {
      setLoading(false);
      toast.error(s('somethingWentWrong'));
    }
  };

  useEffect(() => {
    getWaitingList();
  }, [paid]);

  const targetsFor = (target) =>
    target === 'selected'
      ? waiters.filter((item) => item.toggled)
      : waiters.filter((item) => item.id === target);

  const handleDelete = async (target) => {
    setLoading(true);
    try {
      const partipantsToDelete = targetsFor(target);
      await Promise.all(
        partipantsToDelete.map((participant) =>
          Api.events.deleteWaitingParticipant(participant.id)
        )
      );
    } catch {
      toast.error(epp('participantDeletingERROR'));
    } finally {
      handleModal();
      getWaitingList();
    }
  };
  // One at a time, so each move-in counts the places the ones before took
  const handlePromote = async (target) => {
    setLoading(true);
    const partipantsToPromote = targetsFor(target);

    for (const participant of partipantsToPromote) {
      try {
        // Over the event's places only once the admin says so
        await sendWithOverride((ignoreLimits) =>
          Api.events.promoteEventParticipant(
            participant.id,
            ignoreLimits ? { ignoreLimits } : {}
          )
        );
      } catch (err) {
        // e.g. participant.EventLimitReached: the group doesn't fit
        toast.error(
          `${participant.firstname} ${participant.lastname}: ${paymentErrorMessage(err)}`
        );
      }
    }
    getWaitingList();
    onChanged();
  };

  const sendOffer = async ({ price, bookingFee, expiresAt }) => {
    setOfferBusy(true);
    try {
      const offer = await sendWithOverride((ignoreLimits) =>
        Api.events.offerWaitlistPlace(offering.id, {
          price,
          bookingFee,
          ...(expiresAt && { expiresAt }),
          ...(ignoreLimits && { ignoreLimits }),
        })
      );
      if (offer === OVER_LIMIT_DECLINED) return;
      toast.success(
        ep('offerSentUntil', {
          name: `${offering.firstname} ${offering.lastname}`,
          date: offer?.expiresAt ? formatDateTime(offer.expiresAt) : '-',
        })
      );
      setOffering(null);
      getWaitingList();
      onChanged();
    } catch (err) {
      toast.error(paymentErrorMessage(err));
    } finally {
      setOfferBusy(false);
    }
  };

  const toggleWaiter = (index) => {
    const waitersCopy = [...waiters];
    waitersCopy[index].toggled = !waitersCopy[index].toggled;
    setWaiters(waitersCopy);
  };
  const toggleAll = (_, toggled) =>
    setWaiters(waiters.map((waiter) => ({ ...waiter, toggled })));
  const selectedCount = waiters.filter((waiter) => waiter.toggled).length;
  const allSelected = waiters.length > 0 && selectedCount === waiters.length;

  const renderWaitlist = () => {
    if (!waiters.length) {
      return (
        <tr>
          <td colSpan={5} className="text-muted text-center py-4">
            {p('noWaiters')}
          </td>
        </tr>
      );
    }
    return waiters.map((waiter, i) => {
      // Phone and company sit under the name, like the participant list
      const details = [waiter.phoneNumber, waiter?.company?.name].filter(
        Boolean
      );
      // Their group (them and their guests) must fit the free places
      const fits = groupFits(waiter, freePlaces);
      const offer = offers[waiter.id];
      return (
        <tr key={`waiter-${waiter.id}`}>
          <td className="table-col--check">
            <Checkbox
              index={1}
              toggled={waiter.toggled}
              single
              handleToggle={() => toggleWaiter(i)}
            />
          </td>
          <td className="table-stack__primary">
            {`${waiter.firstname} ${waiter.lastname}`}
            {details.length > 0 && (
              <small className="text-muted d-block">
                {details.join(' · ')}
              </small>
            )}
            {offer && (
              <Badge
                color="info"
                className="mt-1"
                title={formatDateTime(offer.expiresAt)}
              >
                {ep('offeredUntil', {
                  time: moment(offer.expiresAt).fromNow(),
                })}
              </Badge>
            )}
            {!fits && !offer && (
              <small className="event-payments__no-room d-block">
                {ep('notEnoughPlaces', { count: groupSize(waiter) })}
              </small>
            )}
          </td>
          <td data-label={s('date')}>{getBaseDate(waiter.createdAt)}</td>
          <td data-label={s('guests')}>{waiter.friendAmount}</td>
          <td className="table-stack__actions">
            <div className="icon-actions">
              <SquareIconButton
                icon={
                  waiter.additionalNotes ? (
                    <CommentTextOutlineIcon />
                  ) : (
                    <CommentOutlineIcon />
                  )
                }
                active={!!waiter.additionalNotes}
                title={waiter.additionalNotes ? p('editNotes') : p('addNotes')}
                id={`waiter-notes-${waiter.id}`}
                onClick={() => setSelectedWaiter(waiter)}
              />
              {paid && (
                <SquareIconButton
                  icon={<CashIcon />}
                  title={offer ? ep('offerActive') : ep('offerPlace')}
                  tone="primary"
                  id={`waiter-offer-${waiter.id}`}
                  disabled={!!offer}
                  onClick={() => setOffering(waiter)}
                />
              )}
              <SquareIconButton
                icon={<AccountPlusOutlineIcon />}
                title={paid ? ep('moveInFree') : p('promote')}
                tone="primary"
                id={`waiter-promote-${waiter.id}`}
                onClick={() => handlePromote(waiter.id)}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                title={s('delete')}
                tone="danger"
                id={`waiter-delete-${waiter.id}`}
                onClick={() => handleModal(waiter.id)}
              />
            </div>
          </td>
        </tr>
      );
    });
  };

  const handleCloseCommentModal = () => {
    setSelectedWaiter(null);
  };
  const summary = placesSummary(eventCounts, event?.maxAttendees);
  return (
    <>
      {overLimitModal}
      <PaymentOfferModal
        isOpen={!!offering}
        title={ep('offerPlace')}
        description={
          offering
            ? ep('offerDescription', {
                name: `${offering.firstname} ${offering.lastname}`,
              })
            : ''
        }
        event={event}
        currency={currency}
        places={offering ? groupSize(offering) : 1}
        allowDefaultExpiry
        confirmLabel={ep('sendOffer')}
        busy={offerBusy}
        onClose={() => setOffering(null)}
        onConfirm={sendOffer}
      />
      <ShareModal
        visible={!!selectedWaiter}
        title={selectedWaiter?.additionalNotes ? p('editNotes') : p('addNotes')}
        size="md"
        handleModal={handleCloseCommentModal}
      >
        <CommentModal
          selectedParticipantForComment={selectedWaiter}
          handleCloseModal={handleCloseCommentModal}
          getPaticipants={getWaitingList}
          isWaitlist
        />
      </ShareModal>
      <DeleteModal
        visible={deleteVisible}
        handleModal={handleModal}
        type={p('waiter')}
        modalAction={() => handleDelete(deleteTarget)}
      />
      <Loading loading={loading} />
      <FormCard
        title={
          <>
            {p('waitlist')}
            {waiters.length > 0 && (
              <span className="form-card__count">{waiters.length}</span>
            )}
          </>
        }
        actions={
          isSomeoneSelected && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <span className="text-muted small">
                {p('selectedCount', { count: selectedCount })}
              </span>
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                title={s('delete')}
                tone="danger"
                id="waitlist-delete-selected"
                onClick={() => handleModal('selected')}
              />
              <Button
                size="sm"
                color="primary"
                className="mb-0"
                onClick={() => handlePromote('selected')}
              >
                {p('promote')}
              </Button>
            </div>
          )
        }
        className="m-3"
        bodyClassName="form-card__body--flush"
      >
        {(summary || event?.waitlistMode) && (
          <p className="form-card__intro">
            {[
              summary,
              event?.waitlistMode &&
                ep(
                  event.waitlistMode === 'automatic'
                    ? paid
                      ? 'waitlistAutomaticPaidNote'
                      : 'waitlistAutomaticFreeNote'
                    : 'waitlistManualNote'
                ),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th className="table-col--check">
                <Checkbox
                  handleToggle={toggleAll}
                  toggled={allSelected}
                  single
                  index={1}
                />
              </th>
              <th>{s('name')}</th>
              <th>{s('date')}</th>
              <th>{s('guests')}</th>
              <th />
            </tr>
          </thead>
          <tbody>{renderWaitlist()}</tbody>
        </Table>
      </FormCard>
    </>
  );
};

WaitlistPage.propTypes = {
  eventId: PropTypes.string.isRequired,
  event: PropTypes.shape({
    price: PropTypes.number,
    maxAttendees: PropTypes.number,
    waitlistMode: PropTypes.string,
  }),
  eventCounts: PropTypes.shape({
    freePlaces: PropTypes.number,
  }),
  onChanged: PropTypes.func,
};

WaitlistPage.defaultProps = {
  event: null,
  eventCounts: null,
  onChanged: () => {},
};

export default WaitlistPage;
